import { PencilSimpleIcon, PlusIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetSocialLinks } from '@/api/communication/social-links/get-social-links'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Icon } from '@/components/ui/icon'
import { Skeleton } from '@/components/ui/skeleton'
import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { popupFactory } from '@/factories'
import { useDesignVersionToggle } from '@/hooks'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import { AddSocialLinkPopup } from './add-social-link-popup'
import { EditSocialLinkPopup } from './edit-social-link-popup'
import { getServiceOptions, SERVICE_ICON_MAP } from './social-links.constants'
import * as S from './social-links.page.styled'
import { getLinkHref, getServiceLabel } from './social-links.utils'

export const SocialLinksPage = () => {
  const { t } = useTranslation(['communication'])

  const { redirect, toggle: handleUIToggle } = useDesignVersionToggle({
    group: 'social-links',
    screen: 'SocialLinks',
    to: { path: '/dashboard/building-dashboard/social-links/legacy' },
    version: 'new',
  })

  const activeBuilding = useSelector(selectActiveBuilding)
  const buildingId = activeBuilding?.id

  const { data: socialLinks = [], isLoading } = useGetSocialLinks({ buildingId: buildingId ?? 0 })

  const serviceOptions = getServiceOptions(t)
  const existingServices = socialLinks.map((link) => link.service)
  const canAddNew = Boolean(buildingId) && existingServices.length < serviceOptions.length

  const handleAddNew = () => {
    if (!buildingId) return
    popupFactory.open(AddSocialLinkPopup, { buildingId, existingServices })
  }

  const handleEdit = (socialLink: (typeof socialLinks)[number]) => {
    if (!buildingId) return
    popupFactory.open(EditSocialLinkPopup, { buildingId, existingServices, socialLink })
  }

  if (redirect) return redirect

  return (
    <Page>
      <PageHeader
        title={t('communication:socialLinks.title')}
        actions={
          <>
            <ToggleSwitch
              falsyLabel={t('communication:socialLinks.designToggle.old')}
              truthyLabel={t('communication:socialLinks.designToggle.new')}
              checked={true}
              onChange={handleUIToggle}
            />
            <Button size="small" disabled={!canAddNew} onClick={handleAddNew}>
              {t('communication:socialLinks.addNew')}
              <Icon icon={PlusIcon} />
            </Button>
          </>
        }
      />
      <S.Container>
        <S.List>
          {isLoading &&
            Array.from({ length: 3 }).map((_, index) => (
              <S.Item key={index}>
                <Skeleton variant="circular" width="4.8rem" height="4.8rem" />
                <S.SkeletonLinkInfo>
                  <Skeleton variant="text" width="12rem" height="1.4rem" />
                  <Skeleton variant="text" width="22rem" height="1.2rem" />
                </S.SkeletonLinkInfo>
                <S.Actions>
                  <Skeleton variant="circular" width="2.6rem" height="2.6rem" />
                </S.Actions>
              </S.Item>
            ))}

          {!isLoading && socialLinks.length === 0 && (
            <EmptyState
              title={t('communication:socialLinks.empty.title')}
              description={t('communication:socialLinks.empty.description')}
              image={{
                alt: t('communication:socialLinks.empty.imageAlt'),
                src: '/images/illustrations/message-thumbs-up.svg',
                width: '8rem',
              }}
              actions={
                <Button size="small" disabled={!canAddNew} onClick={handleAddNew}>
                  {t('communication:socialLinks.addNew')}
                  <Icon icon={PlusIcon} />
                </Button>
              }
            />
          )}

          {!isLoading &&
            socialLinks.map((socialLink) => (
              <S.Item key={socialLink.id}>
                <S.ServiceIconContainer>
                  <S.ServiceIcon
                    src={SERVICE_ICON_MAP[socialLink.service]}
                    alt={getServiceLabel(socialLink.service, t)}
                  />
                </S.ServiceIconContainer>
                <S.LinkInfo>
                  <S.ServiceName>{getServiceLabel(socialLink.service, t)}</S.ServiceName>
                  <S.LinkUrl
                    href={getLinkHref(socialLink.link)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {socialLink.link}
                  </S.LinkUrl>
                </S.LinkInfo>
                <S.Actions>
                  <Button
                    onlyBox
                    onlyCircle
                    use="ghost"
                    size="small"
                    onClick={() => handleEdit(socialLink)}
                  >
                    <Icon icon={PencilSimpleIcon} size="1.8rem" />
                  </Button>
                </S.Actions>
              </S.Item>
            ))}
        </S.List>
      </S.Container>
    </Page>
  )
}
